import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { api, clearSession, loadSession, persistSession } from '@/lib/api'

export const useAuthStore = defineStore('auth', () => {
  const saved = loadSession()
  const token = ref(saved.token)
  const user = ref(saved.user)
  const loading = ref(false)
  const error = ref('')

  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const role = computed(() => user.value?.role ?? 'GUEST')
  const isGuest = computed(() => role.value === 'GUEST' || !user.value)
  const isAuditor = computed(() => role.value === 'AUDITOR')
  const isAdmin = computed(() => role.value === 'LAB_ENGINEER')

  const canAccessQualification = computed(() => ['AUDITOR', 'LAB_ENGINEER'].includes(role.value))
  const canManageSystem = computed(() => role.value === 'LAB_ENGINEER')
  const canFullFatigue = computed(() => ['AUDITOR', 'LAB_ENGINEER'].includes(role.value))

  async function login(email, password) {
    loading.value = true
    error.value = ''
    try {
      const data = await api('/api/auth/login', { method: 'POST', body: { email, password } })
      token.value = data.token
      user.value = data.user
      persistSession(data.token, data.user)
      return data
    } catch (e) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  async function enterAsGuest() {
    const data = await api('/api/auth/guest', { method: 'POST' })
    token.value = data.token
    user.value = data.user
    persistSession(data.token, data.user)
    return data
  }

  async function logout() {
    try {
      if (token.value) await api('/api/auth/logout', { method: 'POST' })
    } catch {
      // ignore
    }
    token.value = null
    user.value = null
    clearSession()
  }

  function canAccessRoute(meta = {}) {
    const roles = meta.roles
    if (!roles || roles.length === 0) return true
    const current = role.value || 'GUEST'
    return roles.includes(current)
  }

  return {
    token,
    user,
    loading,
    error,
    isAuthenticated,
    role,
    isGuest,
    isAuditor,
    isAdmin,
    canAccessQualification,
    canManageSystem,
    canFullFatigue,
    login,
    enterAsGuest,
    logout,
    canAccessRoute,
  }
})
